import { useState } from 'react';
import styled from 'styled-components';
import { blobErrorText, useLazyDownloadRequestResponseFileQuery } from '@shared/api';
import { formatBytes } from '@shared/lib/formatFile';
import { Button } from '@shared/ui/Button';
import type { RequestResponse } from '@entities/requests';
import { ErrorText } from './parts';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Body = styled.div`
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Meta = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FileRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  font-size: 13.5px;
`;

const FileName = styled.span`
  font-weight: 600;
  overflow-wrap: anywhere;
`;

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  requestId: string;
  response: RequestResponse;
}

/** Ответ исполнителя по заявке: текст, кто и когда ответил, файл на скачивание. */
export function ResponseView({ requestId, response }: Props) {
  const [download, { isFetching }] = useLazyDownloadRequestResponseFileQuery();
  const [error, setError] = useState<string | null>(null);

  async function onDownload() {
    setError(null);
    try {
      const blob = await download(requestId).unwrap();
      const name = blob instanceof File ? blob.name : (response.file_name ?? 'ответ');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(await blobErrorText(err, 'Файл не скачан.'));
    }
  }

  return (
    <Wrap>
      <Body>{response.body}</Body>
      <Meta>
        {response.created_by_name ?? 'Исполнитель'} · {when(response.created_at)}
      </Meta>
      {response.file_name && (
        <FileRow>
          <FileName>{response.file_name}</FileName>
          {response.file_size != null && <Meta>{formatBytes(response.file_size)}</Meta>}
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            onClick={() => void onDownload()}
            disabled={isFetching}
            aria-label={`Скачать ${response.file_name}`}
          >
            {isFetching ? 'Скачивание…' : 'Скачать'}
          </Button>
        </FileRow>
      )}
      {error && <ErrorText role="alert">{error}</ErrorText>}
    </Wrap>
  );
}
